// Centralized configuration for subscription limits and settings
// This file makes it easy to update limits in one place
// Note: Primary pricing configuration is now in ./pricing.ts

export const SUBSCRIPTION_LIMITS = {
  // Free plan limits (14-day trial)
  FREE: {
    VOTES_PER_MONTH: 1000,
    FREE_PLAN_DURATION_DAYS: 14, // Free plan expires after 14 days
    COMMENTS_PER_MONTH: 500,
    SITES: 1,
    EXPORT_CSV: false,
    API_ACCESS: false,
    CUSTOM_BRANDING: false,
    EMAIL_ALERTS: false,
    PRIORITY_SUPPORT: false,
    DEDICATED_SUPPORT: false,
    CUSTOM_INTEGRATIONS: false,
    WHITE_LABEL: false,
    SLA_GUARANTEE: false,
    ADVANCED_ANALYTICS: false,
    BASIC_ANALYTICS: true,
  },
  
  // Pro plan limits ($11.99/month or $115.11/year)
  PRO: {
    VOTES_PER_MONTH: -1, // Unlimited
    COMMENTS_PER_MONTH: -1, // Unlimited
    SITES: -1, // Unlimited
    EXPORT_CSV: true,
    API_ACCESS: true,
    CUSTOM_BRANDING: true,
    EMAIL_ALERTS: true,
    PRIORITY_SUPPORT: true,
    DEDICATED_SUPPORT: false,
    CUSTOM_INTEGRATIONS: false,
    WHITE_LABEL: false,
    SLA_GUARANTEE: false,
    ADVANCED_ANALYTICS: true,
    BASIC_ANALYTICS: true,
  },

  // Enterprise plan limits ($99/month or $948/year)
  ENTERPRISE: {
    VOTES_PER_MONTH: -1, // Unlimited
    COMMENTS_PER_MONTH: -1, // Unlimited
    SITES: -1, // Unlimited
    EXPORT_CSV: true,
    API_ACCESS: true,
    CUSTOM_BRANDING: true,
    EMAIL_ALERTS: true,
    PRIORITY_SUPPORT: true,
    DEDICATED_SUPPORT: true,
    CUSTOM_INTEGRATIONS: true,
    WHITE_LABEL: true,
    SLA_GUARANTEE: true,
    ADVANCED_ANALYTICS: true,
    BASIC_ANALYTICS: true,
  },
} as const;

// Type for limit keys
export type LimitKey = keyof typeof SUBSCRIPTION_LIMITS.FREE;

// Override configuration for specific customers
// Add customer email or site ID to override limits
export const CUSTOMER_OVERRIDES: Record<string, Partial<typeof SUBSCRIPTION_LIMITS.FREE>> = {
  // Example: Override for a specific customer
  // 'customer@example.com': {
  //   VOTES_PER_MONTH: 200,
  //   FREE_PLAN_DURATION_DAYS: 60,
  // },
  
  // Example: Override for a site ID
  // 'site_12345': {
  //   VOTES_PER_MONTH: 500,
  //   COMMENTS_PER_MONTH: 250,
  // },
} as const;

// Helper function to get limits for a specific customer/site
export function getLimitsForCustomer(
  plan: 'free' | 'pro' | 'enterprise',
  customerEmail?: string,
  siteId?: string
): typeof SUBSCRIPTION_LIMITS.FREE | typeof SUBSCRIPTION_LIMITS.PRO | typeof SUBSCRIPTION_LIMITS.ENTERPRISE {
  let baseLimits;
  if (plan === 'enterprise') {
    baseLimits = SUBSCRIPTION_LIMITS.ENTERPRISE;
  } else if (plan === 'pro') {
    baseLimits = SUBSCRIPTION_LIMITS.PRO;
  } else {
    baseLimits = SUBSCRIPTION_LIMITS.FREE;
  }
  
  // Check for customer-specific overrides
  const customerOverride = customerEmail ? CUSTOMER_OVERRIDES[customerEmail] : undefined;
  const siteOverride = siteId ? CUSTOMER_OVERRIDES[siteId] : undefined;
  
  const override = customerOverride || siteOverride;
  
  if (override) {
    return { ...baseLimits, ...override } as typeof baseLimits;
  }
  
  return baseLimits;
}

// Helper function to check if a customer has exceeded their limits
export function checkLimitExceeded(
  plan: 'free' | 'pro' | 'enterprise',
  currentUsage: number,
  limitType: LimitKey,
  customerEmail?: string,
  siteId?: string
): boolean {
  const limits = getLimitsForCustomer(plan, customerEmail, siteId);
  const limit = (limits as any)[limitType];
  
  if (typeof limit === 'number' && limit > 0) {
    return currentUsage >= limit;
  }
  
  return false; // Unlimited or no limit
}

// Helper function to get remaining usage
export function getRemainingUsage(
  plan: 'free' | 'pro' | 'enterprise',
  currentUsage: number,
  limitType: LimitKey,
  customerEmail?: string,
  siteId?: string
): number {
  const limits = getLimitsForCustomer(plan, customerEmail, siteId);
  const limit = (limits as any)[limitType];
  
  if (typeof limit === 'number' && limit > 0) {
    return Math.max(0, limit - currentUsage);
  }
  
  return -1; // Unlimited
} 